import { Account } from '@vendasta/accounts/legacy';
import { FrequentlyAskedQuestionsInterface } from '@vendasta/marketplace-packages';
import { App, Edition } from '@galaxy/marketplace-apps';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { LMI_CATEGORY_ENUM_MAP } from './lmi-categories';
dayjs.extend(utc);

const ENTRY_URL_ACCOUNT_GROUP_PLACEHOLDER = '<accountId>';

function lowerToTitleCase(value: string): string {
  // Code taken from https://github.com/gouch/to-title-case to support the proper spec.
  const smallWords = /^(a|an|and|as|at|but|by|en|for|if|in|nor|of|on|or|per|the|to|vs?\.?|via)$/i;

  return value
    ? value.replace(/[A-Za-z0-9\u00C0-\u00FF]+[^\s-]*/g, (match, index, title) => {
        if (
          index > 0 &&
          index + match.length !== title.length &&
          match.search(smallWords) > -1 &&
          title.charAt(index - 2) !== ':' &&
          (title.charAt(index + match.length) !== '-' || title.charAt(index - 1) === '-') &&
          title.charAt(index - 1).search(/[^\s-]/) < 0
        ) {
          return match.toLowerCase();
        }

        if (match.substr(1).search(/[A-Z]|\../) > -1) {
          return match;
        }

        return match.charAt(0).toUpperCase() + match.substr(1);
      })
    : value;
}

export class FAQ {
  question: string;
  answer: string;
}

export enum EDITION {
  STANDARD = 0,
  EXPRESS,
  PRO,
}

export class File {
  public name: string;
  public icon: string;
  public url: string;

  constructor(url: string) {
    this.name = this._extractName(url);
    this.icon = this._determineIcon(url);
    this.url = url;
  }

  _extractName(url: string): string {
    return url.substring(url.lastIndexOf('/') + 1, url.lastIndexOf('.'));
  }

  _determineIcon(url: string): string {
    const ext = url.substring(url.lastIndexOf('.') + 1);
    switch (ext) {
      case 'pdf':
        return 'icon-pdf';
      case 'ppt':
        return 'icon-ppt';
      default:
        return 'icon-docs';
    }
  }
}

export class Product {
  productId: string;
  editionId?: string;
  name: string;
  editionName?: string;
  active?: boolean;
  abbreviation: string;
  entryUrl?: string;
  iconColor: string;
  iconUrl: string;
  icon: string;
  isTrial: boolean;
  expired: boolean;
  trialDuration?: number;
  trialEnabled?: boolean;
  trialDaysRemaining?: number;
  hiddenFromDashboard?: boolean;

  edition?: EDITION;
  editions?: Edition[];

  tagline: string;
  headerImageUrl: string;
  screenshotUrls: string[];
  files: File[];
  lmiCategories: number[];
  pdfUploadUrls: string[];

  description: string;
  _description: string;

  keySellingPoints: string[];
  _keySellingPoints: string[];

  faqs?: FrequentlyAskedQuestionsInterface[];
  _faqs: FrequentlyAskedQuestionsInterface[];

  getLmiCategoryNames: () => string[];

  endUserMarketingInfo?: {
    description: string;
    keyBenefits: string[];
    faqs: FAQ[];
  };

  usesCustomizationForm?: boolean;

  restrictions?: any;

  static fromV2App(app: App): Product {
    const p = new Product({});

    if (app === new App({})) {
      return p;
    }

    if (app.activationInformation) {
      p.usesCustomizationForm = app.activationInformation.orderFormEnabled;
    }

    if (app.trialConfiguration) {
      p.trialDuration = app.trialConfiguration.trialDuration;
      p.trialEnabled = app.trialConfiguration.trialEnabled;
    }

    if (app.sharedMarketingInformation) {
      p.name = app.sharedMarketingInformation.name;
      p.editionName = app.sharedMarketingInformation.editionName;
      p.icon = app.sharedMarketingInformation.iconUrl;
      p.iconUrl = app.sharedMarketingInformation.iconUrl;
      p.tagline = app.sharedMarketingInformation.tagline;
      p.headerImageUrl = app.sharedMarketingInformation.bannerImageUrl;
    }

    if (app.endUserMarketingInformation) {
      p.description = app.endUserMarketingInformation.description;
      p.keySellingPoints = app.endUserMarketingInformation.keySellingPoints;
      p.faqs = app.endUserMarketingInformation.faqs;
      p.screenshotUrls = app.endUserMarketingInformation.screenshotUrls;
      p.files = app.endUserMarketingInformation.fileUrls?.map((fu) => new File(fu));
    }

    if (app.editionInformation) {
      p.editions = app.editionInformation.editions;
    }

    if (app.key) {
      p.productId = app.key.appId;
      p.editionId = app.key.editionId;
    }

    if (app.basicIntegration) {
      p.hiddenFromDashboard = app.basicIntegration.hideFromClientDashboard;
    }

    if (app.ssoIntegration && app.ssoIntegration.enabled && app.ssoIntegration.entryUrl) {
      p.entryUrl = app.ssoIntegration.entryUrl.replace(
        new RegExp('%3CaccountId%3E', 'g'),
        ENTRY_URL_ACCOUNT_GROUP_PLACEHOLDER,
      );
    } else if (app.basicIntegration && app.basicIntegration.applicationUrl) {
      p.entryUrl = app.basicIntegration.applicationUrl;
    } else if (
      app.key &&
      app.key.appId &&
      app.key.appId.startsWith('CP-') &&
      app.ssoIntegration &&
      app.ssoIntegration.entryUrl
    ) {
      p.entryUrl = app.ssoIntegration.entryUrl.replace(
        new RegExp('%3CaccountId%3E', 'g'),
        ENTRY_URL_ACCOUNT_GROUP_PLACEHOLDER,
      );
    }

    return p;
  }

  constructor(productData: {
    active?: boolean;
    category?: string;
    created?: string | Date;
    currency?: number;
    description?: string;
    endDate?: string | Date;
    entryUrl?: string;
    expiry?: string;
    faqs?: FrequentlyAskedQuestionsInterface[];
    headerImageUrl?: string;
    iconUrl?: string;
    isArchived?: boolean;
    isTrial?: boolean;
    keySellingPoints?: string[];
    lmiCategories?: number[];
    name?: string;
    origin?: string;
    partnerId?: string;
    pdfUploadUrls?: string[];
    productId?: string;
    editionId?: string;
    screenshotUrls?: string[];
    serviceModel?: number[];
    sessionUrl?: string;
    tagline?: string;
    updated?: string | Date;
    updatedBy?: string;
    usesCustomizationForm?: boolean;
    websiteUrl?: string;
    wholesalePrice?: string;
    trialDaysRemaining?: number;
    trialDuration?: number;
    restrictions?: any;
    hiddenFromClientDashboard?: boolean;

    endUserMarketingInfo?: {
      description: string;
      keyBenefits: string[];
      faqs: FAQ[];
    };
  }) {
    this.productId = productData.productId;
    this.name = productData.name;
    this.tagline = productData.tagline;
    this.icon = productData.iconUrl;
    this.iconUrl = productData.iconUrl;
    this.iconColor = Product.getIconColorForName(productData.name);
    this.abbreviation = Product.abbreviation(productData.name);
    this.headerImageUrl = productData.headerImageUrl;
    this.entryUrl = productData.entryUrl;
    this.screenshotUrls = productData.screenshotUrls;
    this.active = productData.active;
    this.files = Product.files(productData);
    this.lmiCategories = productData.lmiCategories || [];
    this.pdfUploadUrls = productData.pdfUploadUrls;
    this.editionId = productData.editionId || '';

    this.endUserMarketingInfo = productData.endUserMarketingInfo;
    this._description = productData.description;
    this._keySellingPoints = productData.keySellingPoints;
    this._faqs = productData.faqs;
    this.isTrial = productData.isTrial;
    this.expired = Product.isProductExpired(productData.expiry);
    this.trialDaysRemaining = productData.trialDaysRemaining;
    this.trialDuration = productData.trialDuration;
    this.description = this.endUserMarketingInfo ? this.endUserMarketingInfo.description : this._description;

    this.restrictions = productData.restrictions;

    this.keySellingPoints = this.endUserMarketingInfo ? this.endUserMarketingInfo.keyBenefits : this._keySellingPoints;

    this.faqs = this.endUserMarketingInfo ? this.endUserMarketingInfo.faqs : this._faqs;

    this.getLmiCategoryNames = () => {
      return this.lmiCategories
        ? this.lmiCategories.map((lmiCategory) =>
            lowerToTitleCase(Product.convertLmiCategoryEnum(lmiCategory).toLowerCase().replace(/_/g, ' ')),
          )
        : [];
    };

    this.usesCustomizationForm = productData.usesCustomizationForm || false;
  }

  static convertLmiCategoryEnum(lmiNumber: number): string {
    return LMI_CATEGORY_ENUM_MAP[lmiNumber];
  }

  static getRelevantFiles(productData: any): string[] {
    return productData.endUserMarketingInfo ? productData.endUserMarketingInfo.files : productData.pdfUploadUrls;
  }

  static files(productData: any): File[] {
    return this.getRelevantFiles(productData) ? this.getRelevantFiles(productData).map((url) => new File(url)) : [];
  }

  static abbreviation(productName: string): string {
    const defaultAbbreviation = 'U'; // untitled
    if (productName) {
      // example input "Partner Central"
      // example output "PC"

      // example input "Partner"
      // example output "P"
      const names = productName.split(' ');
      return names.length > 1 ? names[0][0] + names[1][0] : names[0][0];
    }
    return defaultAbbreviation;
  }

  static getIconColorForName(name: string): string {
    // determine an icon color for a product with no icon by using the product name
    const COLOR_CODES = [
      '#EF5350',
      '#42A5F5',
      '#66BB6A',
      '#FFA726',
      '#AB47BC',
      '#FFCA28',
      '#EC407A',
      '#26C6DA',
      '#FF7B57',
    ];
    let nameSum = 0;
    const defaultColor = '#808080';
    if (!name) {
      return defaultColor;
    }
    for (let i = 0; i < name.length; i++) {
      nameSum += name[i].charCodeAt(0);
    }

    const index = nameSum % COLOR_CODES.length;
    return COLOR_CODES[index];
  }

  static isProductExpired(expiryDate: string): boolean {
    // determines if the expiry date of the product has passed or not,
    // positive difference implies the expiry date has passed
    const now = dayjs().utc();
    const difference = now.diff(expiryDate);
    return difference > 0;
  }

  public renderBusinessIdIntoEntryUrl(businessId: string): void {
    if (this.entryUrl) {
      this.entryUrl = this.entryUrl.replace(new RegExp(ENTRY_URL_ACCOUNT_GROUP_PLACEHOLDER, 'g'), businessId);
    }
  }

  public isAccountOfProduct(a: Account): boolean {
    return a.accountId === this.productId || a.productId === this.productId || a.appId === this.productId;
  }
}
